import React from "react";
import {ChevronLeft, ChevronRight} from "lucide-react";
import * as styles from "./BALevelCarouselV2.module.css";

const s3Location =
	"https://beast-academy-site-assets.s3.us-east-1.amazonaws.com/onboarding/";
const baLevels = [
	{
		id: 1,
		name: "Level 1",
		icon: s3Location + "Level_Icon_BA1.svg",
	},
	{
		id: 2,
		name: "Level 2",
		icon: s3Location + "Level_Icon_BA2.svg",
	},
	{
		id: 3,
		name: "Level 3",
		icon: s3Location + "Level_Icon_BA3.svg",
	},
	{
		id: 4,
		name: "Level 4",
		icon: s3Location + "Level_Icon_BA4.svg",
	},
	{
		id: 5,
		name: "Level 5",
		icon: s3Location + "Level_Icon_BA5.svg",
	},
];

type LevelContent = {
	description: string;
	recommendedAges: [number, number];
};

type Props = {
	selectedLevel?: number;
	onLevelSelect?: (level: number) => void;
	/**
	 * Per-level content rendered inside each card, one entry per level in
	 * order. Optional — if omitted the cards show only the level badge.
	 */
	levels?: LevelContent[];
	/** When true the recommendation line on the *current* card turns red. */
	isAgeMismatched?: boolean;
};

export const BALevelCarousel = ({
	selectedLevel = 1,
	onLevelSelect,
	levels,
	isAgeMismatched = false,
}: Props) => {
	const currentIndex = selectedLevel - 1;
	const currentLevel = baLevels[currentIndex];

	const handlePrevious = () => {
		if (currentIndex > 0) onLevelSelect?.(baLevels[currentIndex - 1].id);
	};

	const handleNext = () => {
		if (currentIndex < baLevels.length - 1)
			onLevelSelect?.(baLevels[currentIndex + 1].id);
	};

	return (
		<div
			className={styles.container}
			role="group"
			aria-label="BA level selection"
		>
			<button
				className={styles.chevronButton}
				onClick={handlePrevious}
				disabled={currentIndex === 0}
				aria-label={`Previous level. Currently on ${currentLevel.name}`}
			>
				<ChevronLeft size={24} strokeWidth={2} aria-hidden />
			</button>

			{/* Viewport clips to one card width; track holds all cards side-by-
			    side and slides horizontally based on the selected index. */}
			<div className={styles.viewport}>
				<div
					className={styles.track}
					style={{
						transform: `translateX(calc(-${currentIndex} * (100% + ${TRACK_GAP_PX}px)))`,
					}}
				>
					{baLevels.map((level, idx) => {
						const content = levels?.[idx];
						const isCurrent = idx === currentIndex;
						return (
							<div
								key={level.id}
								className={styles.cardSlot}
								aria-hidden={!isCurrent}
							>
								<div className={styles.card}>
									<img
										src={level.icon}
										alt={isCurrent ? level.name : ""}
										className={styles.levelIcon}
									/>
									{content?.description && (
										<p className={styles.description}>
											<span className={styles.levelLabel}>
												<b>{level.name}:</b>{" "}
											</span>
											{content.description}
										</p>
									)}
									{content?.recommendedAges && (
										<p
											className={
												isCurrent && isAgeMismatched
													? styles.recommendationMismatch
													: styles.recommendation
											}
										>
											This level is recommended for students ages{" "}
											{content.recommendedAges[0]}–{content.recommendedAges[1]}.
										</p>
									)}
								</div>
							</div>
						);
					})}
				</div>
			</div>

			<button
				className={styles.chevronButton}
				onClick={handleNext}
				disabled={currentIndex === baLevels.length - 1}
				aria-label={`Next level. Currently on ${currentLevel.name}`}
			>
				<ChevronRight size={24} strokeWidth={2} aria-hidden />
			</button>

			<div aria-live="polite" aria-atomic="true" className={styles.srOnly}>
				{currentLevel.name} selected
			</div>
		</div>
	);
};

// Keep in sync with the `gap` on `.track` in the CSS module — used by the
// inline translateX so each slide shifts by one card width plus the gap.
const TRACK_GAP_PX = 24;

export default BALevelCarousel;
